import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { LinkComponent, NavTarget } from '../../shared/components/link/link.component';
import { AvgebaeudeService, FieldMetaData, GebaeudeMasterDetailEntryMeta, GebaeudeVersionDetailEntryMeta } from '../../core/av-api';
import { ListLoader } from '../../shared/components/list-loader/list-loader';
import { TableSortModel } from '../../shared/tablesort/tableSortModel';
import { TimepointService } from '../../core/services/timepoint.service';
import { AppConfigService } from '../../core/services/app-config.service';
import { ComparatorService } from '../../shared/services/comparator-service';
import { FormatConfigService } from '../../core/services/format-config.service';
import { KompaktGebaeudeMasterPipe } from '../../shared/kompaktdarstellung/gebaeude/kompakt-gebaeude-master-pipe';
import { keysToTranslate } from '../../../i18n/keys-to-translate';
import { GrundstueckId } from '../../core/gb-api';
import { map } from 'rxjs/operators';
import { ListLoaderObservable } from '../../shared/components/list-loader/list-loader-observable';
import { HistoryMode } from '../../core/services/HistoryMode';
import { ErweiterungConfigResponse } from '../../core/base-api';
import { AbstractComponent } from '../../shared/components/abstract-component/abstract.component';
import { KompaktVersionGebaeudeeingangsadressePipe } from '../../shared/kompaktdarstellung/gebaeudeeingangsadresse/kompakt-version-gebaeudeeingangsadresse-pipe';
import { HistoryDirective } from '../../shared/directives/history.directive';
import { DecimalHochkommaPipe } from '../../shared/pipes/simple/decimalHochkomma.pipe';
import { KompaktTimepointComponent } from '../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { TranslatePipe } from '@ngx-translate/core';
import { GebaeudeGehoertZuSdrComponent } from '../../shared/components/gebaeude-gehoert-zu-sdr/gebaeude-gehoert-zu-sdr.component';
import { OrderByErstellzeitpunktPipe } from '../../shared/pipes/order/order-by-erstellzeitpunkt.pipe';
import { ColumnHeaderComponent } from '../../shared/tablesort/sort-icon/column-header.component';
import { NgTemplateOutlet } from '@angular/common';
import { ListLoaderWrapperComponent } from '../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { BeschreibungTableWrapperComponent } from '../../shared/components/table/beschreibung-table-wrapper.component';
import { GebaeudeMasterDetailEntryVmService } from '../../shared/services/gebaeude/gebaeude-master-detail-entry-vm.service';
import { GwrLinkComponent } from '../../shared/components/gwr-link/gwr-link.component';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-gebaeude-icp',
  imports: [
    KompaktVersionGebaeudeeingangsadressePipe,
    LinkComponent,
    HistoryDirective,
    DecimalHochkommaPipe,
    KompaktTimepointComponent,
    KompaktGebaeudeMasterPipe,
    TranslatePipe,
    GebaeudeGehoertZuSdrComponent,
    OrderByErstellzeitpunktPipe,
    ColumnHeaderComponent,
    NgTemplateOutlet,
    ListLoaderWrapperComponent,
    BeschreibungTableWrapperComponent,
    GwrLinkComponent
  ],
  templateUrl: './gebaeude-icp.component.html',
  styleUrl: './gebaeude-icp.component.scss'
})
export class GebaeudeIcpComponent extends AbstractComponent implements OnChanges {
  avPlusErweiterung = false;
  navTargets = NavTarget;
  gebaeude: GebaeudeMasterDetailEntryMeta[];
  listLoader: ListLoader<GebaeudeMasterDetailEntryVmService>;
  tableSortModel = new TableSortModel<GebaeudeMasterDetailEntryVmService>('table', this.comparatorService);
  @Input()
  anzeigenAusSichtGeschaeft = false;
  @Input()
  isMeldung = false;
  @Input()
  navigateTo = NavTarget.NAV_GRUNDSTUECK;
  @Input()
  grundstueckId: GrundstueckId;
  showVersichertenNummer = false;
  showGesamtFlaeche = false;
  protected readonly enableNavigation: boolean = false;

  constructor(
    private gebaeudeService: AvgebaeudeService,
    private timepointService: TimepointService,
    private appConfigService: AppConfigService,
    private comparatorService: ComparatorService,
    private appFormatConfigService: FormatConfigService,
    private kompaktGebaeudeMasterPipe: KompaktGebaeudeMasterPipe
  ) {
    super();
    keysToTranslate(['geometrischAuf']);
    this.addSub(
      this.appFormatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.avPlusErweiterung = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.AV_PLUS);
      })
    );
    this.addSub(
      this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
        this.update();
      })
    );
    this.tableSortModel
      .addColumn('egid', (a, b) =>
        comparatorService.orderByGebaeudeComparator(
          a.lastVersion.model.gwrEgid,
          b.lastVersion.model.gwrEgid,
          a.lastVersion.model,
          b.lastVersion.model
        )
      )
      .switchSortDirection();
    this.tableSortModel.addColumn('be-egid', (a, b) =>
      comparatorService.orderByGebaeudeComparator(
        a.lastVersion.model.kantGid,
        b.lastVersion.model.kantGid,
        a.lastVersion.model,
        b.lastVersion.model
      )
    );
    this.tableSortModel.addColumn('versicherungsnummer', (a, b) =>
      comparatorService.orderByGebaeudeComparator(
        a.lastVersion.model.versicherungsNummer,
        b.lastVersion.model.versicherungsNummer,
        a.lastVersion.model,
        b.lastVersion.model
      )
    );
    this.tableSortModel.addColumn('beschreibung', (a, b) =>
      comparatorService.orderByGebaeudeComparator(
        this.getKompaktString(a),
        this.getKompaktString(b),
        a.lastVersion.model,
        b.lastVersion.model
      )
    );
    this.tableSortModel.addColumn('adresse', (a, b) =>
      comparatorService.eingangadressenComparator(a.getRelevantEingangsAdresse, b.getRelevantEingangsAdresse)
    );
    this.tableSortModel.addColumn('teilflaeche', (a, b) =>
      comparatorService.orderByComparator(
        a.lastVersion.gebaeudeAufGrundstueckZuVersion[0]?.flaecheQuadratmeter,
        b.lastVersion.gebaeudeAufGrundstueckZuVersion[0]?.flaecheQuadratmeter
      )
    );
    this.tableSortModel.addColumn('gesamtflaeche', (a, b) =>
      comparatorService.orderByGebaeudeComparator(
        a.lastVersion.model.totalQuadratmeter,
        b.lastVersion.model.totalQuadratmeter,
        a.lastVersion.model,
        b.lastVersion.model
      )
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.grundstueckId?.currentValue) {
      return;
    }
    this.update();
  }

  update() {
    const historischeDaten = this.getHistorischeDaten();
    const timepointCtx = this.timepointService.timepointCtx;
    if (this.grundstueckId) {
      if (this.isMeldung) {
        const gebaeudeObservable = this.gebaeudeService
          .loadGebaeudeSichtGbMeldung(
            //
            this.grundstueckId.id, //
            timepointCtx.timepointIso, //
            timepointCtx.timepointId, //
            historischeDaten //
          )
          .pipe(
            map((gebaeude) => {
              this.gebaeude = gebaeude;
              return this.createViewModel(gebaeude);
            })
          );
        this.listLoader = new ListLoaderObservable<GebaeudeMasterDetailEntryVmService>(undefined, 1, gebaeudeObservable);
        this.listLoader.applySortModel(this.tableSortModel);
      } else {
        const gebaeudeObservable = this.gebaeudeService
          .loadGebaeudeSichtGrundstueck(
            //
            this.grundstueckId.id, //
            timepointCtx.timepointIso, //
            timepointCtx.timepointId, //
            historischeDaten //
          )
          .pipe(
            map((gebaeude) => {
              this.gebaeude = gebaeude;
              return this.createViewModel(gebaeude);
            })
          );
        this.listLoader = new ListLoaderObservable<GebaeudeMasterDetailEntryVmService>(undefined, 1, gebaeudeObservable);
        this.listLoader.applySortModel(this.tableSortModel);
      }
    }
  }

  createViewModel(gebaeude: GebaeudeMasterDetailEntryMeta[]): GebaeudeMasterDetailEntryVmService[] {
    const gebaeudeVM = [];
    for (const geb of gebaeude) {
      const versions = geb.versions;
      geb.versions = null;
      if (versions?.length) {
        const gebaeudeMaster = new GebaeudeMasterDetailEntryVmService(geb, versions, this.comparatorService, this.grundstueckId, true);
        if (gebaeudeMaster.filteredGebaeudeAufGrundstueck?.length > 0) {
          gebaeudeVM.push(gebaeudeMaster);
        }
      }
    }
    this.showVersichertenNummer = gebaeudeVM[0]?.versions[0].mVersicherungsNummer?.access !== FieldMetaData.AccessEnum.DEACTIVATED;
    this.showGesamtFlaeche = gebaeudeVM[0]?.versions[0].mTotalQuadratmeter?.access !== FieldMetaData.AccessEnum.DEACTIVATED;
    return gebaeudeVM;
  }

  getQueryParams(gebaeudeMaster: GebaeudeMasterDetailEntryMeta, gebaeudeVersion: GebaeudeVersionDetailEntryMeta) {
    if (!gebaeudeMaster || !gebaeudeMaster.id) {
      return;
    }
    return { id: gebaeudeMaster.id.id, versionId: gebaeudeVersion.id.id };
  }

  private getKompaktString(gebaeudeVm: GebaeudeMasterDetailEntryVmService) {
    return this.kompaktGebaeudeMasterPipe.transform(gebaeudeVm.lastVersion.model, null);
  }

  private getHistorischeDaten() {
    if (this.anzeigenAusSichtGeschaeft && this.appConfigService.einstellungHistorischeDaten === HistoryMode.HistoryMode.OHNE_HIST) {
      return HistoryMode.HistoryMode.OHNE_HIST_MIT_GESCHAEFTSAENDERUNGEN;
    }
    return this.appConfigService.einstellungHistorischeDaten;
  }
}
